import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCategoriesAndBrands, getCategoriesAndBrandsScoped, searchProducts } from '../services/productService';
import { getCategories } from '../services/categoryService';
import pageService from '../services/pageService';
import PageRenderer from '../components/PageRenderer';
import Filters from '../components/Filters/Filters';
import * as filterService from '../services/filterService';
import { priceInfo } from '../utils/priceUtils';
import API_BASE_URL from '../config';
import ProductCard from '../components/ProductCard';
import ProductListCard from '../components/ProductListCard';

export default function PublicProducts() {
  const location = useLocation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [searchQ, setSearchQ] = useState('');
  const searchDebounce = useRef(null);
  const [page, setPage] = useState(() => {
    const p = new URLSearchParams(location.search).get('page');
    return p && Number(p) > 0 ? Number(p) : 1;
  });
  const [perPage, setPerPage] = useState(() => {
    const pp = new URLSearchParams(location.search).get('perPage');
    return pp && Number(pp) > 0 ? Number(pp) : 20;
  });
  const [filters, setFilters] = useState({});
  const [meta, setMeta] = useState({ categories: [], brands: [] });
  const [categoriesTree, setCategoriesTree] = useState([]);
  const [initialFilters, setInitialFilters] = useState({});
  const [injectedModules, setInjectedModules] = useState([]);
  const [injectedSidebarModules, setInjectedSidebarModules] = useState([]);
  const [filterVisibility, setFilterVisibility] = useState({
    showCategories: true,
    showFocus: true,
    showBrand: true,
    showPrice: true,
    showWarranty: true,
    showRating: true,
    showSpecs: true,
    showStock: true,
    stockStatusLabel: 'Vetëm në stok',
    stockStatusDefault: 'unselected',
  });
  const [viewMode, setViewMode] = useState('grid');
  const [sortOrder, setSortOrder] = useState(() => {
    const so = new URLSearchParams(location.search).get('sort');
    return so || 'default';
  });
  const [inStockOnly, setInStockOnly] = useState(() => {
    const params = new URLSearchParams(location.search);
    const inStock = params.get('inStock');
    if (inStock !== null) return inStock === 'true';
    const oos = params.get('includeOutOfStock');
    if (oos !== null) return oos === 'false';
    return false;
  });
  const inStockOnlyRef = useRef(inStockOnly);
  useEffect(() => { inStockOnlyRef.current = inStockOnly; }, [inStockOnly]);
  const urlInitRef = useRef(false);
  const categoriesLoadedRef = useRef(false);

  const normalizeFiltersForCompare = useCallback((f = {}) => {
    const out = { ...f };
    if (out.brand && Array.isArray(out.brand)) out.brand = out.brand.slice().sort();
    if (out.specs && typeof out.specs === 'object') {
      const specOut = {};
      Object.entries(out.specs).forEach(([k, v]) => {
        if (Array.isArray(v)) specOut[k] = v.slice().sort();
        else if (v !== undefined && v !== null && String(v) !== '') specOut[k] = v;
      });
      out.specs = specOut;
    }
    return out;
  }, []);

  const parseFiltersFromSearch = useCallback((search) => {
    const params = new URLSearchParams(search || '');
    const getBool = (v) => {
      if (v === null || v === undefined) return false;
      return ['1', 'true', 'yes', 'on'].includes(String(v).toLowerCase());
    };

    const q = params.get('q') || '';
    const parsed = {};

    const category = params.get('category');
    const categoryId = params.get('categoryId');
    if (category) {
      parsed.category = category;
      parsed.categoryName = category;
    } else if (categoryId) {
      parsed.category = categoryId;
    }

    const brandParams = params.getAll('brand').filter(Boolean);
    if (brandParams.length > 1) {
      parsed.brand = brandParams;
    } else if (brandParams.length === 1) {
      const b = brandParams[0];
      parsed.brand = b.includes(',') ? b.split(',').map(s => s.trim()).filter(Boolean) : b;
    }

    if (params.get('priceMin')) parsed.priceMin = params.get('priceMin');
    if (params.get('priceMax')) parsed.priceMax = params.get('priceMax');
    if (params.get('warranty')) parsed.warranty = params.get('warranty');
    if (params.get('ratingMin')) parsed.ratingMin = params.get('ratingMin');
    if (params.get('offer') !== null) parsed.offer = getBool(params.get('offer'));
    if (params.get('isNew') !== null) parsed.isNew = getBool(params.get('isNew'));
    if (params.get('specialPrice') !== null) parsed.specialPrice = getBool(params.get('specialPrice'));

    const specs = {};
    params.forEach((value, key) => {
      if (!key.startsWith('spec_')) return;
      const specKey = key.slice(5);
      if (!specKey) return;
      if (!specs[specKey]) specs[specKey] = [];
      specs[specKey].push(value);
    });
    if (Object.keys(specs).length) parsed.specs = specs;

    return { filters: parsed, q };
  }, []);

  const pageRef = useRef(page);
  const perPageRef = useRef(perPage);
  const sortOrderRef = useRef(sortOrder);
  useEffect(() => { pageRef.current = page; }, [page]);
  useEffect(() => { perPageRef.current = perPage; }, [perPage]);
  useEffect(() => { sortOrderRef.current = sortOrder; }, [sortOrder]);

  const syncUrlFromFilters = useCallback((f = {}, q = '', opts = {}) => {
    const params = filterService.buildParams(f);
    if (q) params.q = q;

    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      if (Array.isArray(v)) v.forEach(item => { if (item !== undefined && item !== null && item !== '') qs.append(k, item); });
      else qs.append(k, v);
    });

    // Persist pagination & sort state in URL
    const pg = opts.page !== undefined ? opts.page : pageRef.current;
    const pp = opts.perPage !== undefined ? opts.perPage : perPageRef.current;
    const so = opts.sort !== undefined ? opts.sort : sortOrderRef.current;
    const iso = opts.inStock !== undefined ? opts.inStock : inStockOnlyRef.current;
    if (pg && pg > 1) qs.set('page', pg);
    if (pp && pp !== 20) qs.set('perPage', pp);
    if (so && so !== 'default') qs.set('sort', so);
    const defaultChecked = filterVisibility.stockStatusDefault === 'selected';
    if (iso !== defaultChecked) {
      qs.set('inStock', iso ? 'true' : 'false');
    }

    const query = qs.toString();
    const url = query ? `/products?${query}` : '/products';
    navigate(url, { replace: true });
  }, [navigate, filterVisibility.stockStatusDefault]);

  // Use refs to break dependency cycle in loadProducts
  const filtersRef = useRef(filters);
  const searchQRef = useRef(searchQ);

  useEffect(() => { filtersRef.current = filters; }, [filters]);
  useEffect(() => { searchQRef.current = searchQ; }, [searchQ]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/settings`);
        const data = await res.json().catch(() => ({}));
        if (!mounted) return;
        if (data && data.filter_settings) {
          const parsed = typeof data.filter_settings === 'string'
            ? JSON.parse(data.filter_settings)
            : data.filter_settings;
          if (parsed && typeof parsed === 'object') {
            const normalized = {
              ...parsed,
              stockStatusLabel: (parsed.stockStatusLabel && parsed.stockStatusLabel !== 'Shfaq produktet jashtë stokut')
                ? parsed.stockStatusLabel
                : 'Vetëm në stok',
              stockStatusDefault: parsed.stockStatusDefault || 'unselected',
            };
            setFilterVisibility(prev => ({ ...prev, ...normalized }));

            // If inStock / includeOutOfStock is not in URL, apply admin configured default state
            const currentUrlParam = new URLSearchParams(window.location.search);
            const hasParam = currentUrlParam.get('inStock') !== null || currentUrlParam.get('includeOutOfStock') !== null;
            if (!hasParam) {
              const defaultChecked = normalized.stockStatusDefault === 'selected';
              setInStockOnly(defaultChecked);
            }
          }
        }
      } catch (_) {
        // ignore
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Track last successfully loaded params to avoid echoes
  const lastLoadedParamsRef = useRef(null);
  const abortControllerRef = useRef(null);

  const loadProducts = useCallback(async (opts = {}, replace = false) => {
    try {
      // Use ref current value to avoid recreating callback when filters change
      const mergedFilters = replace ? opts : { ...filtersRef.current, ...opts };
      const params = filterService.buildParams(mergedFilters);
      
      // Clone params and strip spec_ filters so the API returns all products in the category
      const apiParams = { ...params };
      Object.keys(apiParams).forEach(k => {
        if (k.startsWith('spec_')) {
          delete apiParams[k];
        }
      });
      apiParams.includeOutOfStock = 'true';
      if (opts.q !== undefined) apiParams.q = opts.q;
      else if (searchQRef.current) apiParams.q = searchQRef.current;

      const qs = new URLSearchParams();
      Object.entries(apiParams || {}).forEach(([k, v]) => {
        if (v === undefined || v === null || v === '') return;
        if (Array.isArray(v)) v.forEach(item => { if (item !== undefined && item !== null && item !== '') qs.append(k, item); });
        else qs.append(k, v);
      });
      const paramsKey = qs.toString();

      // If this request is identical to what we JUST successfully loaded, skip it.
      // This handles the case where Filters component initializes and "echoes" back the same state.
      // We only skip if there is NO active controller (meaning pending request).
      // If there IS a pending request, we might want to let this new one override it (debounce effect)
      // or we might realize it's the exact same intent.
      if (!abortControllerRef.current && lastLoadedParamsRef.current === paramsKey) {
        return;
      }

      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      const prods = await searchProducts(apiParams, { signal: controller.signal });

      // Request success
      abortControllerRef.current = null;
      lastLoadedParamsRef.current = paramsKey;

      setProducts(prods || []);
    } catch (e) {
      if (e.name === 'AbortError') return;
      console.error('Load products failed', e);
      // specific error state? setProducts([])? 
      // Keeping validation simple for now
      abortControllerRef.current = null;
    }
  }, []);

  // Combined Initial Load & URL Sync Effect
  useEffect(() => {
    let mounted = true;
    urlInitRef.current = false;

    const initPage = async () => {
      try {
        // Always fetch categories tree on mount
        if (!categoriesLoadedRef.current) {
          const tree = await getCategories();
          if (!mounted) return;
          setCategoriesTree(tree || []);
          categoriesLoadedRef.current = true;
        }

        const parsed = parseFiltersFromSearch(location.search || '');
        const initFilters = parsed.filters || {};
        const q = parsed.q || '';
        const params = new URLSearchParams(location.search);
        const inStockParam = params.get('inStock');
        const oosParam = params.get('includeOutOfStock');
        if (inStockParam !== null) {
          setInStockOnly(inStockParam === 'true');
        } else if (oosParam !== null) {
          setInStockOnly(oosParam === 'false');
        } else {
          setInStockOnly(filterVisibility.stockStatusDefault === 'selected');
        }
        const cat = initFilters.categoryName || initFilters.category || '';
        const brand = initFilters.brand || '';

        // Safely extract just the exact category ID or slug string without URL params
        const safeCatSlug = cat ? encodeURIComponent(String(cat).trim().toLowerCase().replace(/\s+/g, '-')) : null;
        const safeBrandSlug = brand ? encodeURIComponent(String(brand).trim().toLowerCase().replace(/\s+/g, '-')) : null;

        // Fetch injected admin page modules for this route
        const allowedInjectionTypes = ['popup', 'promo-strip', 'announcement', 'head-tags'];
        const allowedSidebarTypes = ['vertical-banner'];

        // Build the EXACT slug
        let exactSlug = 'products';
        if (safeCatSlug) exactSlug += `-category-${safeCatSlug}`;
        if (safeBrandSlug) exactSlug += `-brand-${safeBrandSlug}`;

        const isGlobal = !safeCatSlug && !safeBrandSlug;
        if (isGlobal) exactSlug += `-global`; // Only if absolutely no filters

        const pagePromises = [pageService.getPageBySlug(exactSlug).catch(() => null)];
        if (!isGlobal) {
          pagePromises.push(pageService.getPageBySlug('products-global').catch(() => null));
        }

        const [specificPageData, globalPageData] = await Promise.all(pagePromises);

        if (mounted) {
          const parseContent = (data) => {
            if (!data || !data.content) return [];
            if (typeof data.content === 'string') {
              try { return JSON.parse(data.content); } catch (e) { return []; }
            } else if (Array.isArray(data.content)) {
              return data.content;
            }
            return [];
          };

          const globalModules = isGlobal ? [] : parseContent(globalPageData);
          const specificModules = parseContent(specificPageData);

          const seenIds = new Set();
          let modulesArray = [...globalModules, ...specificModules].filter(m => {
            if (!m.id) return true;
            if (seenIds.has(m.id)) return false;
            seenIds.add(m.id);
            return true;
          });

          const filteredInjects = modulesArray.filter(m => allowedInjectionTypes.includes(m.type) && !m.hidden);
          const filteredSidebar = modulesArray.filter(m => allowedSidebarTypes.includes(m.type) && !m.hidden);
          setInjectedModules(filteredInjects);
          setInjectedSidebarModules(filteredSidebar);
        }

        // Evaluate changes BEFORE any async/awaits so we don't race with React state updates

        let metaPromise;

        if (q) {
          setSearchQ(q);
          // If search is active, we might want all brands or scoped? Usually all if no category.
          metaPromise = getCategoriesAndBrands({ includeOutOfStock: true });
        } else {
          setSearchQ('');
          if (cat) {
            metaPromise = getCategoriesAndBrandsScoped(cat, { includeOutOfStock: true });
          } else {
            metaPromise = getCategoriesAndBrands({ includeOutOfStock: true });
          }
        }

        // Load metadata
        const metaRes = await metaPromise;
        if (!mounted) return;
        const availableBrands = Array.isArray(metaRes?.brands) ? metaRes.brands : [];
        setMeta(metaRes || { categories: [], brands: [] });

        // Validate brands against current scope
        let validBrand = initFilters.brand;
        let brandsChanged = false;

        if (validBrand) {
          const currentBrands = Array.isArray(validBrand) ? validBrand : [validBrand];
          const filtered = currentBrands.filter(b => availableBrands.includes(b));
          if (filtered.length !== currentBrands.length) {
            validBrand = filtered.length === 0 ? undefined : (filtered.length === 1 ? filtered[0] : filtered);
            brandsChanged = true;
          }
        }

        const validFilters = { ...initFilters };
        if (brandsChanged) {
          if (validBrand === undefined) delete validFilters.brand;
          else validFilters.brand = validBrand;
        }

        const sameValidFilters = JSON.stringify(normalizeFiltersForCompare(filtersRef.current || {})) === JSON.stringify(normalizeFiltersForCompare(validFilters || {}));

        if (!sameValidFilters) {
          setFilters(validFilters);
          setInitialFilters(validFilters);

          if (brandsChanged) {
            // Update URL to remove invalid brands
            syncUrlFromFilters(validFilters, q);
          }
        }

        // Always attempt to load products based on the current URL params.
        // loadProducts internal check (lastLoadedParamsRef) will prevent redundant fetches.
        loadProducts({ ...validFilters, q }, true);

        urlInitRef.current = true;

      } catch (err) {
        console.error('Init page failed', err);
      }
    };

    initPage();

    return () => { mounted = false; };
  }, [location.search, loadProducts, normalizeFiltersForCompare, parseFiltersFromSearch, syncUrlFromFilters, filterVisibility.stockStatusDefault]);

  // Sorting Logic
  const getSortedProducts = () => {
    let sorted = [...products];

    // Client-side specs filtering
    const activeSpecs = filters.specs || {};
    if (Object.keys(activeSpecs).length > 0) {
      sorted = sorted.filter(p => {
        const prodSpecs = p && p.specs ? p.specs : {};
        // Every selected spec category must match (AND condition)
        return Object.entries(activeSpecs).every(([specKey, selectedValues]) => {
          if (!Array.isArray(selectedValues) || selectedValues.length === 0) return true;
          // Product must have a spec value that matches one of the selected values (OR condition)
          const prodSpecVal = String(prodSpecs[specKey] ?? '').toLowerCase().trim();
          return selectedValues.some(v => String(v).toLowerCase().trim() === prodSpecVal);
        });
      });
    }

    if (inStockOnly) {
      sorted = sorted.filter(p => p && p.stock != null && Number(p.stock) > 0);
    }
    if (sortOrder === 'price-asc') {
      sorted.sort((a, b) => {
        const pA = priceInfo(a).display;
        const pB = priceInfo(b).display;
        return pA - pB;
      });
    } else if (sortOrder === 'price-desc') {
      sorted.sort((a, b) => {
        const pA = priceInfo(a).display;
        const pB = priceInfo(b).display;
        return pB - pA;
      });
    }
    return sorted;
  };

  const sortedProducts = getSortedProducts();
  const total = sortedProducts.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const current = Math.min(page, totalPages);
  const start = (current - 1) * perPage;
  const end = start + perPage;
  const pageItems = sortedProducts.slice(start, end);

  const handleFilterChange = useCallback((f) => {
    setFilters(f);
    // Only reset the page when the user actively changes a filter AFTER
    // initialization. During init, Filters echoes the URL state back via
    // onChange — those echoes must not reset the page the user navigated back to.
    if (urlInitRef.current) {
      setPage(1);
    }
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      if (urlInitRef.current) {
        syncUrlFromFilters(f, searchQRef.current || '', { page: 1 });
      }
    }, 250);
  }, [syncUrlFromFilters]);

  return (
    <div className="page-container">
      {/* Inject floating admin modules (popups, announcements) here */}
      {injectedModules.length > 0 && (
        <PageRenderer content={injectedModules} pageType="global" />
      )}

      <div className={`sidebar-layout ${injectedSidebarModules.length > 0 ? 'has-right-sidebar' : ''}`}>
        <aside style={{ padding: 8 }}>
          <Filters
            initial={initialFilters}
            categories={categoriesTree}
            brands={Array.isArray(meta.brands) ? meta.brands : []}
            enabledSections={filterVisibility}
            // pass computed specs metadata (top keys + their values)
            specsMeta={(() => {
              try {
                const deepestId = filters.category_child2 || filters.category_child1 || filters.category || '';
                
                const findCategoryInTree = (nodes, idOrName) => {
                  if (!idOrName) return null;
                  const target = String(idOrName).toLowerCase().trim();
                  for (const n of nodes || []) {
                    if (String(n.id) === target || String(n.name).toLowerCase().trim() === target) {
                      return n;
                    }
                    const children = n.subcategories || n.children;
                    if (children && children.length) {
                      const found = findCategoryInTree(children, idOrName);
                      if (found) return found;
                    }
                  }
                  return null;
                };

                const selectedCat = findCategoryInTree(categoriesTree, deepestId);
                const definedSpecs = (selectedCat && Array.isArray(selectedCat.specs)) ? selectedCat.specs : null;

                const counts = {};
                const values = {};
                for (const p of products || []) {
                  const s = p && p.specs ? p.specs : null;
                  if (!s || typeof s !== 'object') continue;
                  for (const [k, v] of Object.entries(s)) {
                    const key = String(k).trim();
                    if (!key) continue;
                    counts[key] = (counts[key] || 0) + 1;
                    values[key] = values[key] || new Set();
                    if (v !== undefined && v !== null && String(v).trim() !== '') {
                      values[key].add(String(v).trim());
                    }
                  }
                }

                if (definedSpecs && definedSpecs.length > 0) {
                  return definedSpecs.map(k => {
                    const key = String(k).trim();
                    return {
                      key,
                      count: counts[key] || 0,
                      values: Array.from(values[key] || [])
                    };
                  });
                }

                // pick top 5 keys by count
                const keys = Object.keys(counts).sort((a, b) => counts[b] - counts[a]).slice(0, 5);
                return keys.map(k => ({ key: k, count: counts[k], values: Array.from(values[k] || []) }));
              } catch (e) { return []; }
            })()}
            onChange={handleFilterChange}
          />
        </aside>

        {/* Style block for responsive grid */}
        <style>{`
          .sidebar-layout.has-right-sidebar {
             grid-template-columns: 360px 1fr 260px;
          }
          @media (max-width: 900px) {
             .sidebar-layout.has-right-sidebar {
                 grid-template-columns: 1fr;
             }
             .right-banner-sidebar {
                 display: none; /* Optionally hide the banner on mobile, or let it stack */
             }
          }
          .products-grid-container {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
            gap: 20px;
          }
          @media (max-width: 768px) {
            .products-grid-container {
              grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
              gap: 12px;
            }
          }
          .products-card.list {
             display: flex;
             flex-direction: row;
             width: 100%;
          }
          @media (max-width: 768px) {
            .hide-on-mobile {
              display: none !important;
            }
            .pagination-container {
              justify-content: center !important;
            }
          }
          .products-controls {
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            gap: 16px;
          }
          .controls-left-section {
            display: flex;
            align-items: center;
            gap: 16px;
            flex-wrap: wrap;
          }
          .controls-right-section {
            display: flex;
            align-items: center;
            gap: 10px;
          }
          @media (max-width: 768px) {
            .products-controls {
              flex-direction: column;
              align-items: stretch;
              gap: 12px;
            }
            .controls-left-section {
              flex-direction: column;
              align-items: stretch;
              gap: 12px;
            }
            .controls-right-section {
              justify-content: flex-end;
            }
          }

        `}</style>

        <main style={{ paddingRight: injectedSidebarModules.length === 0 ? 16 : 0 }}>
          {/* Controls Bar */}
          <div className="products-controls" style={{ marginBottom: 20, background: '#fff', padding: '12px 20px', borderRadius: 8, border: '1px solid #eee', boxShadow: '0 2px 5px rgba(0,0,0,0.03)' }}>
            <div className="controls-left-section">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontWeight: 600, color: '#555', fontSize: 14 }}>Rendit sipas:</span>
                <div style={{ position: 'relative' }}>
                  <select
                    id="sortOrder"
                    name="sortOrder"
                    value={sortOrder}
                    onChange={(e) => { const so = e.target.value; setSortOrder(so); if (urlInitRef.current) syncUrlFromFilters(filtersRef.current || {}, searchQRef.current || '', { sort: so }); }}
                    aria-label="Rendit produktet"
                    style={{
                      appearance: 'none',
                      padding: '8px 32px 8px 12px',
                      borderRadius: 6,
                      border: '1px solid #ddd',
                      outline: 'none',
                      fontSize: 14,
                      cursor: 'pointer',
                      background: '#fff url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23333%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E") no-repeat right 10px center',
                      backgroundSize: '10px'
                    }}
                  >
                    <option value="default">Parazgjedhur</option>
                    <option value="price-asc">Çmimi më i ulët</option>
                    <option value="price-desc">Çmimi më i lartë</option>
                  </select>
                </div>
              </div>
              {filterVisibility.showStock !== false && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#555', cursor: 'pointer', fontWeight: 500, userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={inStockOnly}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setInStockOnly(checked);
                      if (urlInitRef.current) syncUrlFromFilters(filtersRef.current || {}, searchQRef.current || '', { inStock: checked });
                    }}
                    style={{ cursor: 'pointer', width: 15, height: 15 }}
                  />
                  {filterVisibility.stockStatusLabel || 'Vetëm në stok'}
                </label>
              )}
            </div>
            <div className="controls-right-section">
              <button
                onClick={() => setViewMode('grid')}
                title="Grid View"
                aria-label="Pamje me rrjetë"
                style={{
                  background: viewMode === 'grid' ? '#e6f0fa' : 'transparent',
                  color: viewMode === 'grid' ? '#0b79d0' : '#666',
                  border: '1px solid',
                  borderColor: viewMode === 'grid' ? '#0b79d0' : '#ddd',
                  padding: 6,
                  borderRadius: 4,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>
              </button>
              <button
                onClick={() => setViewMode('list')}
                title="List View"
                aria-label="Pamje si listë"
                style={{
                  background: viewMode === 'list' ? '#e6f0fa' : 'transparent',
                  color: viewMode === 'list' ? '#0b79d0' : '#666',
                  border: '1px solid',
                  borderColor: viewMode === 'list' ? '#0b79d0' : '#ddd',
                  padding: 6,
                  borderRadius: 4,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>
              </button>
            </div>
          </div>

          {/* Products Container */}
          <div className={viewMode === 'grid' ? 'products-grid-container' : ''} style={{
            display: viewMode === 'list' ? 'flex' : undefined,
            flexDirection: viewMode === 'list' ? 'column' : undefined,
            gap: 20
          }}>
            {pageItems.map((p) => {
              return viewMode === 'grid' ? (
                <ProductCard key={p.id} product={p} />
              ) : (
                <ProductListCard key={p.id} product={p} />
              );
            })}
          </div>

          <div className="pagination-container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 32, padding: '20px 0', borderTop: '1px solid #eee' }}>
            <div className="hide-on-mobile" style={{ display: 'flex', gap: 12, alignItems: 'center', color: '#666', fontSize: 14 }}>
              <div>Shfaq</div>
              <select id="perPage" name="perPage" value={perPage} onChange={(e) => { const pp = Number(e.target.value); setPerPage(pp); setPage(1); if (urlInitRef.current) syncUrlFromFilters(filtersRef.current || {}, searchQRef.current || '', { perPage: pp, page: 1 }); }} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ddd' }}>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <div>produkte</div>
              <div style={{ marginLeft: 16, borderLeft: '1px solid #ddd', paddingLeft: 16 }}>
                <strong>{total}</strong> produkte u gjetën
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button onClick={() => { const np = Math.max(1, page - 1); setPage(np); if (urlInitRef.current) syncUrlFromFilters(filtersRef.current || {}, searchQRef.current || '', { page: np }); }} disabled={page === 1} style={{ padding: '6px 12px', border: '1px solid #ddd', background: page === 1 ? '#f5f5f5' : '#fff', borderRadius: 4, cursor: page === 1 ? 'default' : 'pointer', color: page === 1 ? '#999' : '#333' }}>Para</button>
              <div style={{ fontSize: 14, color: '#333' }}>Faqja {page} nga {totalPages}</div>
              <button onClick={() => { const np = Math.min(totalPages, page + 1); setPage(np); if (urlInitRef.current) syncUrlFromFilters(filtersRef.current || {}, searchQRef.current || '', { page: np }); }} disabled={page === totalPages} style={{ padding: '6px 12px', border: '1px solid #ddd', background: page === totalPages ? '#f5f5f5' : '#fff', borderRadius: 4, cursor: page === totalPages ? 'default' : 'pointer', color: page === totalPages ? '#999' : '#333' }}>Pas</button>
            </div>
          </div>

        </main>

        {injectedSidebarModules.length > 0 && (
          <aside className="right-banner-sidebar" style={{ padding: '8px 8px 8px 0' }}>
            <PageRenderer content={injectedSidebarModules} pageType="sidebar" />
          </aside>
        )}
      </div>
    </div>
  );
}

